import { useCreateBriefBulkDeliverables } from "@entities/brief/lib/hooks/useCreateBriefDeliverables";
import { useUpdateBriefDeliverable } from "@entities/brief/lib/hooks/useUpdateBriefDeliverable";
import { useUpdateBriefForm } from "@entities/brief/lib/hooks/useUpdateBriefForm";
import useGetDeliverableConfigurators from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableConfigurators";
import useGetDeliverableMediaRights from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableMediaRights";
import { DeliverableFormats, ILocalDeliverable } from "@entities/deliverableFormats/ui/DeliverableFormats";
import { ToastService } from "@shared/lib/services";
import { IDelPackage, IPurchaseItems } from "@shared/lib/types";
import { IBriefDeliverable } from "@shared/lib/types/briefInfluencerV2";
import {
  Button,
  Card,
  Col,
  Row,
  Typography
} from "@shared/ui";
import { Divider, Flex, Select } from "antd";
import TextArea from "antd/es/input/TextArea";
import {
  Check
} from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./PackageForm.module.scss";

const { Option } = Select;

const generateObjectId = () => {
  const timestamp = Math.floor(new Date().getTime() / 1000).toString(16).padStart(8, '0');
  const random = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return timestamp + random;
};

export default function PackageForm({
  showTitle,
  selectedPackage,
  isDefault,
  briefId,
  isEdit,
  isClone,
  onCancel,
  onBack,
  onSuccess,
}: {
  showTitle?: boolean;
  selectedPackage?: IDelPackage;
  isDefault?: boolean;
  briefId: string;
  onCancel?: () => void;
  onBack?: () => void;
  onSuccess?: (deliverableData?: IBriefDeliverable) => void;
  isEdit: boolean;
  isClone: boolean;
}) {
  const [deliverables, setDeliverables] = useState<ILocalDeliverable[]>([]);
  const [isDefaultDeliverable, setIsDefaultDeliverable] = useState<boolean>(
    isEdit || isClone ? isDefault || false : false
  );
  const [notes, setNotes] = useState<string>("");
  const [packageName, setPackageName] = useState<string>(
    isEdit || isClone ? selectedPackage?.name || "" : ""
  );
  const [generatedName, setGeneratedName] = useState<string>("");
  const [customDays, setCustomDays] = useState<number | null>(null);
  const [popoverOpenIndex, setPopoverOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (onBack) {
          onBack();
        } else {
          onCancel?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [onBack, onCancel]);

  useEffect(() => {
    if (!packageName) {
      const deliverablesWithAdRights: Record<string, number> = {};
      const deliverablesWithoutAdRights: Record<string, number> = {};

      deliverables.forEach((deliverable) => {
        if (deliverable.contentCategory) {
          if (deliverable.purchaseItemType === "Ad-Rights") {
            deliverablesWithAdRights[deliverable.contentCategory] =
              (deliverablesWithAdRights[deliverable.contentCategory] || 0) + 1;
          } else {
            deliverablesWithoutAdRights[deliverable.contentCategory] =
              (deliverablesWithoutAdRights[deliverable.contentCategory] || 0) + 1;
          }
        }
      });

      const nameParts = [
        ...Object.entries(deliverablesWithAdRights).map(
          ([category, count]) => `${count} x ${category}(Ad Rights)`
        ),
        ...Object.entries(deliverablesWithoutAdRights).map(
          ([category, count]) => `${count} x ${category}`
        ),
      ];

      setGeneratedName(nameParts.join(", "));
    }
  }, [packageName, deliverables]);

  const { deliverableConfiguratorsData, isDeliverableConfiguratorsFetching } =
    useGetDeliverableConfigurators();
  const { deliverableMediaRightsData, isDeliverableMediaRightsFetching } =
    useGetDeliverableMediaRights();

  const { createBulk, isCreatingBulk, isCreatedBulk, deliverablesData } =
    useCreateBriefBulkDeliverables();
  const { updateForm } = useUpdateBriefForm();
  const { update, isUpdating, isUpdated } = useUpdateBriefDeliverable();

  useEffect(() => {
    setPackageName(isEdit || isClone ? selectedPackage?.name || "" : "");
    setIsDefaultDeliverable(isEdit || isClone ? isDefault || false : false);
  }, [isEdit, isClone, selectedPackage, isDefault]);
  useEffect(() => {
    if (isCreatedBulk || isUpdated) {
      if (isCreatedBulk) {
        onSuccess?.(deliverablesData?.data?.[0]?.deliverableV2);
      } else {
        onSuccess?.();
      }
      onCancel?.();
    }
  }, [isCreatedBulk, isUpdated]);

  useEffect(() => {
    if (isCreatedBulk && briefId) {
      updateForm({
        form: { isDeliverablesCompleted: true },
        briefId: briefId,
      });
    }
  }, [isCreatedBulk, briefId]);

  const configurators = deliverableConfiguratorsData?.data?.data || [];

  useEffect(() => {
    if ((isEdit || isClone) && selectedPackage) {
      const parsedItems: ILocalDeliverable[] = [];
      const adRightsGroupMap: { [key: string]: ILocalDeliverable } = {};

      (selectedPackage.purchaseItems || []).forEach((item) => {
        if (item.purchaseItemType === "Ad-Rights") {
          const key = `${item.adRightsType || ""}-${item.adRightsDurations || ""}-${item.adRightsNotes || ""}`;
          const linkedId = getLinkedContentId(item);
          if (adRightsGroupMap[key]) {
            if (linkedId) {
              adRightsGroupMap[key].adRightsLinkedToContentPurchaseItemId= linkedId;
            }
          } else {
            const newItem: ILocalDeliverable = {
              clientSideId: item._id || Math.random().toString(36).substring(2, 9),
              _id: item._id,
              purchaseItemType: "Ad-Rights",
              contentCategory: "Ad Rights",
              contentSubType: item.contentSubType || {},
              contentType: item.contentType || "",
              adRightsDurations: item.adRightsDurations || "",
              adRightsNotes: item.adRightsNotes || "",
              adRightsType: item.adRightsType || "",
              adRightsDescription: item.adRightsDescription || "",
              contentDeliverableConfiguratorId: getConfiguratorId(item),
              contentDeliverableConfiguratorDeliverableId: getDeliverableId(item),
              adRightsLinkedToContentPurchaseItemId: linkedId || undefined,
            };
            adRightsGroupMap[key] = newItem;
            parsedItems.push(newItem);
          }
        } else {
          parsedItems.push({
            clientSideId: item._id || Math.random().toString(36).substring(2, 9),
            _id: item._id,
            purchaseItemType: "Content",
            contentCategory: item.contentCategory || "",
            contentSubType: item.contentSubType || {},
            contentType: item.contentType || "",
            adRightsDurations: item.adRightsDurations || "",
            adRightsNotes: item.adRightsNotes || "",
            adRightsType: item.adRightsType || "",
            adRightsDescription: item.adRightsDescription || "",
            contentDeliverableConfiguratorId: getConfiguratorId(item),
            contentDeliverableConfiguratorDeliverableId: getDeliverableId(item),
          });
        }
      });

      const packageDeliverables =
        parsedItems.length > 0
          ? parsedItems
          : [];
      setDeliverables(packageDeliverables);
      setNotes(selectedPackage.notes || "");
    }
  }, [isEdit, isClone, selectedPackage]);

  const getConfiguratorId = (item: IPurchaseItems) => {
    return item.contentDeliverableConfiguratorId || "";
  };

  const getDeliverableId = (item: IPurchaseItems) => {
    return item.contentDeliverableConfiguratorDeliverableId || "";
  };

  const getLinkedContentId = (item: IPurchaseItems) => {
    return item.adRightsLinkedToContentPurchaseItemId || "";
  };

  const preparePayload = () => {
    console.log("Dels: ", deliverables);
    if (deliverables.length === 0) {
      ToastService.error("Please add at least one deliverable.");
      return null;
    }
    if (
      deliverables.some(
        (d) =>
          d.purchaseItemType === "Content" &&
          (!d.contentDeliverableConfiguratorId ||
            !d.contentDeliverableConfiguratorDeliverableId)
      )
    ) {
      ToastService.error("Please fill all deliverable fields.");
      return null;
    }

    for (const d of deliverables) {
      if (
        d.purchaseItemType === "Ad-Rights" &&
        (!d.adRightsType || !d.adRightsDurations)
      ) {
        ToastService.error(
          `Please fill all Ad Rights fields.`
        );
        return null;
      }
    }

    const finalPurchaseItems: any[] = [];
    const contentMapByClientSideId: { [key: string]: any } = {};

    // 1. Add all Content format items to finalPurchaseItems first
    deliverables.forEach((d) => {
      if (d.purchaseItemType === "Content") {
        const item = {
          _id: d._id || generateObjectId(),
          purchaseItemType: "Content",
          contentCategory: d.contentCategory || "",
          contentSubType: d.contentSubType || {},
          contentType: d.contentType || "",
          contentDeliverableConfiguratorId: d.contentDeliverableConfiguratorId,
          contentDeliverableConfiguratorDeliverableId: d.contentDeliverableConfiguratorDeliverableId,
          contentDeliverableConfigurator: d.contentDeliverableConfiguratorId
        };
        finalPurchaseItems.push(item);
        contentMapByClientSideId[d.clientSideId] = item;
      }
    });

    // 2. Add Ad-Rights items
    deliverables.forEach((d) => {
      if (d.purchaseItemType === "Ad-Rights") {
        const adRightsType = d.adRightsType || "";
        const adRightsDurations = d.adRightsDurations || "";
        const adRightsNotes = d.adRightsNotes || "";
        const adRightsDescription =
          d.adRightsType === "Custom"
            ? d.adRightsNotes
            : deliverableMediaRightsData?.data?.types.find(
                (t: { name: string; description?: string }) =>
                  t.name === d.adRightsType
              )?.description || "";

        const baseAdRightsId = d._id || generateObjectId();
        const baseAdRightsItem: any = {
          purchaseItemType: "Ad-Rights",
          adRightsType,
          adRightsDurations,
          adRightsNotes,
          adRightsDescription,
          _id: baseAdRightsId,
        };

        const rawLinkedId = d.adRightsLinkedToContentPurchaseItemId || "";
        const linkedContentItem = rawLinkedId ? contentMapByClientSideId[rawLinkedId] : undefined;
        const linkedId = linkedContentItem ? linkedContentItem._id : rawLinkedId;
        finalPurchaseItems.push({
          ...baseAdRightsItem,
          adRightsLinkedToContentPurchaseItemId: linkedId || undefined,
        });
      }
    });

    return {
      notes,
      purchaseItems: finalPurchaseItems,
      setDefaultDeliverable: isDefaultDeliverable || undefined,
      name: packageName || undefined,
      briefId,
    };
  };

  const handleSave = () => {
    const payload = preparePayload();
    if (!payload) return;

    if (isEdit && selectedPackage?._id) {
      update({ ...payload, deliverableV2Id: selectedPackage._id });
    } else {
      createBulk({ bulkDeliverables: [payload], briefId });
    }
  };

  const handleAddDeliverable = () => {
    setDeliverables((prev) => [
      ...prev,
      {
        clientSideId: Math.random().toString(36).substring(2, 9),
        purchaseItemType: "Content",
        contentCategory: "",
        contentSubType: {},
        contentType: "",
        adRightsDurations: "",
        adRightsNotes: "",
        adRightsType: "",
        adRightsDescription: "",
        contentDeliverableConfiguratorId: "",
        contentDeliverableConfiguratorDeliverableId: "",
        adRightsLinkedToContentPurchaseItemId: undefined,
        isExisting: false,
      },
    ]);
  };

  const handleRemoveDeliverable = (index: number) => {
    setDeliverables((prev) => {
      const itemToRemove = prev[index];
      return prev
        .filter((_, i) => i !== index)
        .map((del) => {
          if (
            del.purchaseItemType === "Ad-Rights" &&
            itemToRemove &&
            (del.adRightsLinkedToContentPurchaseItemId === itemToRemove.clientSideId ||
              (itemToRemove._id && del.adRightsLinkedToContentPurchaseItemId === itemToRemove._id))
          ) {
            return {
              ...del,
              adRightsLinkedToContentPurchaseItemId: undefined,
            };
          }
          return del;
        });
    });
  };

  const handleSelectionChange = (index: number, value: string) => {
    setDeliverables((prev) => {
      const targetItem = prev[index];
      return prev.map((del, i) => {
        if (i === index) {
          if (value === "Ad-Rights") {
            return {
              clientSideId: del.clientSideId || Math.random().toString(36).substring(2, 9),
              purchaseItemType: "Ad-Rights",
              contentCategory: "Ad Rights",
              contentSubType: {},
              contentType: "",
              adRightsDurations: "",
              adRightsNotes: "",
              adRightsType: "",
              adRightsDescription: "",
              contentDeliverableConfiguratorId: "",
              contentDeliverableConfiguratorDeliverableId: "",
              adRightsLinkedToContentPurchaseItemId: undefined,
              isExisting: false,
            };
          }
          const [configuratorId, deliverableId] = value.split("|");
          const delConfig = configurators.find((cfg) => cfg._id === configuratorId);
          const format = delConfig?.deliverables.find((fmt) => fmt._id === deliverableId);

          return {
            clientSideId: del.clientSideId || Math.random().toString(36).substring(2, 9),
            purchaseItemType: "Content",
            contentCategory: format?.category || "",
            contentSubType: {},
            contentType: "",
            adRightsDurations: "",
            adRightsNotes: "",
            adRightsType: "",
            adRightsDescription: "",
            contentDeliverableConfiguratorId: configuratorId,
            contentDeliverableConfiguratorDeliverableId: deliverableId,
            adRightsLinkedToContentPurchaseItemId: undefined,
            isExisting: false,
          };
        }

        if (
          value === "Ad-Rights" &&
          del.purchaseItemType === "Ad-Rights" &&
          targetItem &&
          (del.adRightsLinkedToContentPurchaseItemId === targetItem.clientSideId ||
            (targetItem._id && del.adRightsLinkedToContentPurchaseItemId === targetItem._id))
        ) {
          return {
            ...del,
            adRightsLinkedToContentPurchaseItemId: undefined,
          };
        }

        return del;
      });
    });
  };

  const handleValueChange = (index: number, field: string, value: any) => {
    setDeliverables((prev) =>
      prev.map((del, i) => {
        if (i === index) {
          const newDel = { ...del, [field]: value };
          if (field === "adRightsType" && value !== "Custom") {
            newDel.adRightsNotes = "";
          }
          return newDel;
        }
        return del;
      })
    );
  };

  const handleSubTypeValueChange = (index: number, subTypeName: string, value: string | undefined) => {
    setDeliverables((prev) =>
      prev.map((del, i) => {
        if (i === index) {
          const newSubTypes = { ...del.contentSubType };
          if (value === undefined) {
            delete newSubTypes[subTypeName];
          } else {
            newSubTypes[subTypeName] = value;
          }
          return { ...del, contentSubType: newSubTypes };
        }
        return del;
      })
    );
  };

  return (
    <Card
      className={styles.packageCard}
      style={{ width: "100%" }}
      title={
        <Flex vertical gap={10}>
          {showTitle ? (
            <Flex gap={2} justify="space-between" align="center">
              <span className={styles.headerLabel}>New Deliverables</span>
              <Button
                onClick={() => (onBack ? onBack() : onCancel?.())}
                type="text"
                size="small"
                className={styles.headerLabel}
              >Back</Button>
            </Flex>
          ) : null}
          {/* <Flex gap={10}>
            <Star size={18} fill="#f59e0b" color="#f59e0b" />
            <Input
              bordered={false}
              placeholder="Enter Package Name"
              value={packageName || generatedName}
              onChange={(e) => setPackageName(e.target.value)}
              style={{
                height: 20,
                padding: 0,
                width: "100%",
                borderBottom: "1px solid #e3e8ee",
              }}
            />
            {!showTitle ? (
              <Button
                icon={<X size={16} />}
                size="small"
                type="text"
                onClick={() => onCancel?.()}
              />
            ) : null}
          </Flex> */}
          {/* <Tooltip
            placement="bottom"
            title="By setting this Deliverable set as default, all the influencer's that are added would have this deliverable, the user can still assign other deliverables after they are added."
          >
            <Tag.CheckableTag
              checked={isDefaultDeliverable}
              onChange={(checked) => setIsDefaultDeliverable(checked)}
              style={{ width: "fit-content" }}
              className={
                isDefaultDeliverable
                  ? styles.deliverableTagChecked
                  : styles.deliverableTag
              }
            >
              <Flex align="center" gap={4}>
                {isDefaultDeliverable ? (
                  <Zap size={10} />
                ) : (
                  <Circle size={10} />
                )}
                DEFAULT DELIVERABLE
              </Flex>
            </Tag.CheckableTag>
          </Tooltip> */}
        </Flex>
      }
    >
      <Flex vertical gap={8} style={{ width: "100%" }}>
        <DeliverableFormats
          deliverables={deliverables}
          onChange={setDeliverables}
          onAdd={handleAddDeliverable}
          onRemove={handleRemoveDeliverable}
          onSelectionChange={handleSelectionChange}
          onValueChange={handleValueChange}
          onSubTypeValueChange={handleSubTypeValueChange}
          minItemsToRestrictDelete={1}
        />
        <Divider style={{margin: 0}}/>
        <Row>
          <Col span={24}>
            <Typography.Text
              className={styles.headerLabel}>
              Special Instructions
            </Typography.Text>
            <TextArea
              placeholder="E.g., Specific hashtags, caption requirements..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 6 }}
              style={{ marginTop: 5 }}
            />
          </Col>
        </Row>

        {generatedName || packageName ? (
          <Typography.Text
            style={{
              fontSize: 11,
              color: "#64748b",
              fontWeight: 500,
              display: "block",
              wordBreak: "break-word",
            }}
          >
            {packageName || generatedName}
          </Typography.Text>
        ) : null}

        <div className={styles.formActions}>
          {!showTitle ? (
            <Button
              size="small"
              onClick={onCancel}
              disabled={isUpdating || isCreatingBulk}
              className={styles.formActionButton}
            >
              Cancel
            </Button>
          ) : null}
          <Button
            type="primary"
            size="small"
            onClick={handleSave}
            loading={isUpdating || isCreatingBulk}
            icon={<Check size={14} />}
            className={styles.formActionButton}
          >
            {isEdit ? "Save Deliverable Set" : "Create Deliverable Set"}
          </Button>
        </div>
      </Flex>
    </Card>
  );
}
